import React, { useCallback, useEffect, useState } from 'react';
import Header from '../Header';
import api from '../../api/axios';
import { listManagedIdps, showIdp, updateIdpManager } from '../../api/idps';
import IdpFormManager from '../../components/idp/IdpFormManager';
import { useNavigate } from 'react-router-dom';

export default function TeamIdps() {
    const nav = useNavigate();
    const [authUser, setAuthUser] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [perPage] = useState(15);
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });

    // Manager editing a specific IDP (inline modal-ish card)
    const [editingIdp, setEditingIdp] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const loadUser = useCallback(async () => {
        try {
            const { data } = await api.get('/api/user');
            setAuthUser(data);
        } catch {
            // ignore for now; handled via auth guard elsewhere
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listManagedIdps({
                q: q || undefined,
                page,
                per_page: perPage,
            });
            const payload = res?.data || {};
            setRows(Array.isArray(payload?.data) ? payload.data : []);
            setMeta({
                current_page: payload.current_page ?? 1,
                last_page: payload.last_page ?? 1,
                total: payload.total ?? (payload?.data?.length ?? 0),
            });
        } catch (e) {
            setError('Failed to load team IDPs.');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, q]);

    useEffect(() => { loadUser(); }, [loadUser]);
    useEffect(() => { load(); }, [load]);

    const beginManagerEdit = async (idpId) => {
        try {
            const idp = await showIdp(idpId);
            setEditingIdp(idp);
        } catch {
            setError('Failed to open IDP.');
        }
    };

    const cancelManagerEdit = () => setEditingIdp(null);

    const submitManagerEdit = async (form) => {
        if (!editingIdp) return;
        setSaving(true);
        setError('');
        try {
            await updateIdpManager(editingIdp.id, {
                manager_support: form.manager_support ?? null,
                date_of_next_meeting: form.date_of_next_meeting || null,
            });
            setEditingIdp(null);
            await load();
        } catch (e) {
            setError(e?.response?.data?.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const canPrev = meta.current_page > 1;
    const canNext = meta.current_page < meta.last_page;
    const renderIndex = (idx) => String((meta.current_page - 1) * perPage + idx + 1).padStart(2, '0');

    return (
        <>
            <Header label={<p className="py-0 my-0">Team Individual Development Plans</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">

                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="d-flex flex-column">
                                    <h5 className="mb-0">Direct Reports — Active Cycle</h5>
                                    <small className="text-white">
                                        {authUser ? `Manager: ${authUser.name}` : ''}
                                    </small>
                                </div>
                                <input
                                    className="form-control form-control-sm"
                                    style={{ width: 280 }}
                                    placeholder="Search by name or email…"
                                    value={q}
                                    onChange={e => { setPage(1); setQ(e.target.value); }}
                                />
                            </div>
                            <div className="card-body">
                                {error && <div className="alert alert-danger m-3">{error}</div>}
                                {loading ? (
                                    <div className="text-muted p-3">Loading…</div>
                                ) : (

                                        <div className="table-responsive reviews-table reviews-table--zebra">

                                        <table className="table table-sm table-hover  mb-0">
                                            <thead className="table-light">
                                            <tr>
                                                <th>Employee</th>
                                                <th>Email</th>
                                                <th style={{ width: 130 }} className="text-center">Has Contract</th>
                                                <th style={{ width: 130 }} className="text-center">IDP Count</th>
                                                <th style={{ width: 200 }} className="text-end">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {rows.length === 0 && (
                                                <tr><td colSpan="5" className="text-center text-muted py-4">No direct reports.</td></tr>
                                            )}
                                            {rows.map((r, idx) => (
                                                <tr key={r.user.id}>
                                                    <td className="fw-semibold">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="text-muted small">{renderIndex(idx)}</span>
                                                            <span>
                                                                {[r.user.name, r.user.surname].filter(Boolean).join(' ') || '—'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="text-muted">{r.user.email}</td>
                                                    <td className="text-center">
                                                        {r.has_contract ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}
                                                    </td>
                                                    <td className="text-center">
                              <span className={`badge ${r.idp_count > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                                {r.idp_count}
                              </span>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="btn-group btn-group-sm">
                                                            <button
                                                                className="btn btn-outline-primary"
                                                                disabled={!r.contract}
                                                                onClick={() => nav(`/idp/contract/${r.contract?.id}`)}
                                                                title={!r.contract ? 'No contract' : 'View IDPs'}
                                                            >
                                                                View IDPs
                                                            </button>
                                                            {/* Optional: if your manager wants to edit a specific IDP, you can list them on a detail page.
                                    For now, this button expects the manager to know an IDP to open or you can add a drawer later. */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {!loading && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted small">
                                            Page {meta.current_page} of {meta.last_page} • Total {meta.total}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={!canPrev}
                                                onClick={() => canPrev && setPage(p => p - 1)}
                                            >
                                                Prev
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={!canNext}
                                                onClick={() => canNext && setPage(p => p + 1)}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Inline manager edit card (appears when an IDP is opened for manager fields) */}
                        {editingIdp && (
                            <div className="card mt-3 bg-light">
                                <div className="card-body">
                                    <h6 className="mb-3">
                                        Update Manager Fields — {editingIdp?.contract?.user?.name || 'Employee'}
                                    </h6>
                                    <IdpFormManager
                                        initial={{
                                            manager_support: editingIdp.manager_support,
                                            date_of_next_meeting: editingIdp.date_of_next_meeting,
                                        }}
                                        onSubmit={submitManagerEdit}
                                        onCancel={cancelManagerEdit}
                                        submitting={saving}
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </main>
        </>
    );
}
